import moment from "moment";

type SortOrder = "asc" | "desc";

/** MUI DataTables sortCompare factory for date/time values. */
export const compareDates =
  (order: string) =>
  (a: { data: unknown }, b: { data: unknown }) => {
    const timeA = moment(a.data as string).valueOf() || 0;
    const timeB = moment(b.data as string).valueOf() || 0;
    return order === "asc" ? timeA - timeB : timeB - timeA;
  };

/** MUI DataTables sortCompare factory for numeric values. */
export const compareNumbers =
  (order: string) =>
  (a: { data: unknown }, b: { data: unknown }) => {
    const numA = Number(a.data) || 0;
    const numB = Number(b.data) || 0;
    return order === "asc" ? numA - numB : numB - numA;
  };

/** Parse currency strings like "CAD 123.45" or plain numbers. */
export const parseAmountValue = (value: unknown): number => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return value;
  const str = String(value);
  const match = str.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  return match ? parseFloat(match[0]) : 0;
};

/** MUI DataTables sortCompare factory for currency / amount strings. */
export const compareAmounts =
  (order: string) =>
  (a: { data: unknown }, b: { data: unknown }) => {
    const numA = parseAmountValue(a.data);
    const numB = parseAmountValue(b.data);
    return order === "asc" ? numA - numB : numB - numA;
  };

/** MUI DataTables sortCompare factory for plain strings (case-insensitive). */
export const compareStrings =
  (order: string) =>
  (a: { data: unknown }, b: { data: unknown }) => {
    const strA = String(a.data ?? "").toLowerCase();
    const strB = String(b.data ?? "").toLowerCase();
    if (strA < strB) return order === "asc" ? -1 : 1;
    if (strA > strB) return order === "asc" ? 1 : -1;
    return 0;
  };

export const sortDisabled = { sort: false as const };

export const dateColumnOptions = (direction: SortOrder = "desc") => ({
  sortDirection: direction,
  sortCompare: compareDates,
});

export const amountColumnOptions = {
  sortCompare: compareAmounts,
};

export const numberColumnOptions = {
  sortCompare: compareNumbers,
};

const NON_SORTABLE_COLUMNS = new Set([
  "action",
  "actions",
  "edit",
  "star",
  "enable",
  "receipt",
  "refund",
  "logo",
  "instrument_logo",
  "invoice",
  "download_offline",
  "transaction_status",
  "EnabledDisable",
  "note",
  "download",
  "body",
  "message",
  "school_name",
  "username",
  "category",
  "school_id",
  "read",
  "view",
  "delete",
  "options",
  "icon",
  "image",
  "status_badge",
]);

const DATE_COLUMNS = new Set([
  "date",
  "timestamp",
  "upload_date",
  "date_created",
  "schedule",
  "last_date",
  "DateAdded",
  "dateInitiate",
  "dateComplete",
  "created_at",
  "sent_timestamp",
  "holiday_date",
  "start_date",
  "end_date",
  "payment_date",
  "transaction_date",
  "last_updated",
  "updated_at",
  "created_date",
  "expiry_date",
  "due_date",
  "bill_date",
  "refund_date",
  "login_date",
  "last_login",
  "reg_date",
  "dateInitiate",
  "dateComplete",
  "paid_on",
  "date",
  "date_time",
  "request_date",
]);

const AMOUNT_COLUMNS = new Set([
  "amount",
  "course_fee",
  "total",
  "balance",
  "refund_amount",
  "paid_amount",
  "due_amount",
  "tax_amount",
  "subtotal",
  "price",
  "fee",
  "total_amount",
  "payment_amount",
]);

const NUMBER_COLUMNS = new Set([
  "total_hours",
  "incab_training_hours",
  "inclass_training_hours",
  "inyard_training_hours",
  "hours",
  "quantity",
  "count",
  "sr_no",
]);

export const stringColumnOptions = {
  sortCompare: compareStrings,
};

const TEXT_COLUMNS = new Set([
  "student_name",
  "student_email",
  "mobile",
  "program_name",
  "course_name",
  "template_name",
  "receipts_name",
  "fee_type",
  "transaction_id",
  "action_type",
  "device_ip",
  "browser",
  "user_agent",
  "instructor_name",
  "asset_name",
  "type_of_class",
  "paymentgateway_id",
  "transaction_acc",
  "paymentStatus",
  "class_name",
  "feedback_message",
  "username",
  "name",
  "email",
  "title",
  "description",
  "category",
  "subject",
  "school_name",
  "staff_name",
  "first_name",
  "last_name",
  "full_name",
]);

/** Apply consistent sort options to MUI DataTable column definitions. */
export function enhanceTableColumns(columns: any[]): any[] {
  return columns.map((col) => {
    const name = String(col.name ?? "");
    const opts = { ...(col.options || {}) };

    if (opts.sort === false) {
      return col;
    }

    if (NON_SORTABLE_COLUMNS.has(name)) {
      return { ...col, options: { ...opts, sort: false } };
    }

    if (DATE_COLUMNS.has(name) && !opts.sortCompare) {
      const direction = (opts.sortDirection as SortOrder) || "desc";
      return {
        ...col,
        options: { ...opts, ...dateColumnOptions(direction) },
      };
    }

    if (AMOUNT_COLUMNS.has(name) && !opts.sortCompare) {
      return { ...col, options: { ...opts, ...amountColumnOptions } };
    }

    if (NUMBER_COLUMNS.has(name) && !opts.sortCompare) {
      return { ...col, options: { ...opts, ...numberColumnOptions } };
    }

    if (TEXT_COLUMNS.has(name) && !opts.sortCompare) {
      return { ...col, options: { ...opts, ...stringColumnOptions } };
    }

    return col;
  });
}
